import { sha1 } from 'object-hash';
import {
  AUTH0_CLIENT_ID,
  DELETE_HIDDEN_COLUMNS,
  DELETE_MULTIPLE_COLUMNS,
  DELETE_SELECTED_COLUMNS,
  DELETE_VISIBLE_COLUMNS,
  EVENT_NAME_GIGASHEET_FILTER_PROGRESS,
  FILE,
  FILES,
  FILTER,
  MODAL_DELETE_ROW,
  MODAL_MAKE_COPY,
  PERMISSION_WRITE,
  PIVOT,
  QUERY_SELECTOR_FORMULA_PANEL_BTN,
  ROW_COUNT_FAILED,
  SIDE_PANEL_ASSISTANT_PANEL,
  SIDE_PANEL_COLUMN_PANEL,
  SIDE_PANEL_ROWS_PANEL,
  SIDE_PANEL_STATS_PANEL,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  URL_DATASET,
} from '..//Utils/constants';
import { getFileUuidFromPath } from '..//Utils/getFileUuidFromPath';
import { getFilterType } from '..//Utils/getFilterType';
import AggregationFunctions from '../Components/Spreadsheet/AggregationFunctions/AggregationFunctions';
import FunctionSelect from '../Components/Spreadsheet/AggregationFunctions/FunctionSelect';
import CustomAutoGroupValue from '../Components/TableGrid/CustomAutoGroupValue';
import CustomColumnHeader from '../Components/TableGrid/CustomColumnHeader';
import CustomNoRowsComponent from '../Components/TableGrid/CustomNoRowsComponent';
import { httpDeleteWithBody, post, postRaw, put, streamPost } from '../Utils/API';
import { fetchOpenedFile } from '../redux/reducers/datasetSlice';
import { showModal } from '../redux/reducers/modalsSlice';
import { addMixpanelEvent } from '../redux/reducers/sharedSlice';
import {
  addToast,
  deleteColumns,
  restoreGridState,
  setReduxFilterModelCnf,
  setGenerateDeletingRows,
  setIsDeletingRows,
  setIsInsertingNewColumn,
  setNewColumnMode,
  setRowsNumber,
  setInsertNewColumnNextTo,
  setResetFormulaForm,
  setAppliedFiltersMap,
  setFormulaToEdit,
  setColumnHighlights,
  setTotalGroupsCount,
  setLoadingRowCount,
  setIsRequestingRows,
  setFilterProgressIsRunning,
} from '../redux/reducers/spreadsheetSlice';
import { store } from '../redux/store';
import { generateArrayFromRowsIndex } from '@/Utils/spreadsheetGenerateArrayFromRowsIndex';
import getTopRangeRowsIndexAndRowCount from '@/Utils/getTopRangeRowsIndexAndRowCount';
import { AG_GRID_AUTO_COLUMN, ROW_NUMBER_COLUMN_ID } from '@/Utils/gridConstants';
import getCurrentAppliedFilters from '@/Utils/getCurrentAppliedFilters';
import { authenticationSliceName } from '@/redux/constants';
import { StatusCodes } from 'http-status-codes';
import ShowToast from '@/Components/Toast/showToastTemplate';
import { ExtractTokenDetails } from '@/Utils/Auth0TokenUtils.tsx';
import DotsLoader from '@/Components/Loader/DotsLoader';
import { FILE_TYPE_EXTERNAL } from '@/Utils/fileConstants';

export const getRowClassRules = () => ({
  'flag-none': (params) => {
    const flag = params.data?.metadata_tag ? +params.data.metadata_tag : 0;
    return flag === 0;
  },
  'flag-good': (params) => {
    const flag = params.data?.metadata_tag ? +params.data.metadata_tag : 0;
    return flag === 1;
  },
  'flag-suspicious': (params) => {
    const flag = params.data?.metadata_tag ? +params.data.metadata_tag : 0;
    return flag === 2;
  },
  'flag-bad': (params) => {
    const flag = params.data?.metadata_tag ? +params.data.metadata_tag : 0;
    return flag === 3;
  },
});

export const icons = {
  rows: () => {
    return `
  <div id="${SIDE_PANEL_ROWS_PANEL}" class="sidebar-icon" data-cy="row-details-icon">
    <span class="sidebar-tooltip">Rows</span>
    <div class="row_detail"></div>
  </div>
`;
  },
  columnsIcon: () => {
    return `
    <div id="${SIDE_PANEL_COLUMN_PANEL}" class="sidebar-icon">
        <span class="sidebar-tooltip">Columns</span>
        <div class="columns_details"/>
      </div>
      `;
  },
  statsIcon: () => {
    return `
    <div id="${SIDE_PANEL_STATS_PANEL}" class="sidebar-icon">
        <span class="sidebar-tooltip">Sheet Stats</span>
        <div class="stats_detail"/>
      </div>`;
  },
  sheetAssistantIcon: () => {
    return `
    <div id="${SIDE_PANEL_ASSISTANT_PANEL}" class="sidebar-icon">
        <span class="sidebar-tooltip">Sheet Assistant</span>
        <div class="sheet-assistant"/>
      </div>`;
  },
};

export const getRowId = (params) =>
  sha1({
    data: params.data,
    parentKeys: params.parentKeys,
  });

export const getDataSource = (agGrid, silentRefresh, logout) => {
  return {
    colDefHash: null,
    state: null,
    getRows: async function (params) {
      const state = store.getState();
      const { paginationPageSize } = state.spreadsheet;
      const { currentFile } = state.dataset;
      const isLiveQuery = currentFile?.metadata?.Type === FILE_TYPE_EXTERNAL;
      const isPivotMode = params?.request?.pivotMode && params?.request?.pivotCols?.length;

      // check if this request is for getting child rows of an expanded group
      const isChildRowsRequest = params?.parentNode?.level > -1;

      if (!isChildRowsRequest) {
        store.dispatch(setLoadingRowCount(true));
      }

      const doFail = () => {
        if (!isChildRowsRequest) {
          store.dispatch(setLoadingRowCount(false));
        }
        return params.fail();
      };

      const getPaginationData = (payload, isLastPage, lastRow, rowData) => {
        try {
          if (isChildRowsRequest) {
            return;
          }
          const fileId = getFileUuidFromPath();
          const groupsPayload = {
            ...payload,
            groupKeys: [],
            rowGroupCols: payload?.rowGroupCols?.[0] ? [payload?.rowGroupCols?.[0]] : [],
          };

          const isPivotMode = params?.request?.pivotMode && params?.request?.pivotCols?.length > 0;
          const updateCounts = async (filteredRowCount, filteredGroupCount = null) => {
            const groupCount = isPivotMode ? lastRow : filteredGroupCount;
            const totalNumberOfRows = currentFile?.metadata?.FileRows;

            store.dispatch(
              setRowsNumber({
                // used to calculate totalNumberOfPages displayed in the paging bottom element: Math.ceil((totalGroupCount > 0 ? totalGroupCount : filteredNumberOfRows) / paginationPageSize)
                filteredNumberOfRows: filteredRowCount,
                // controls how the "Rows" part in the paging bottom element is displayed. If filteredNumberOfRows < totalNumberOfRows
                // then it will be "Rows: filteredNumberOfRows of totalNumberOfRows". Otherwise, it will be just "Rows: totalNumberOfRows"
                totalNumberOfRows: totalNumberOfRows,
              })
            );
            // sets redux variable "totalGroupCount" which controls how the "Groups" part in the paging bottom element is displayed: "Groups: totalGroupCount".
            // If totalGroupCount is set to null, then it won't show the "Groups" part at all.
            store.dispatch(setTotalGroupsCount(groupCount));

            // This is required for "go to last page" and "go to page" to work correctly (AgGrid calculates page numbers based on total nr of rows)
            if (filteredGroupCount) {
              let rowCount;
              if (isPivotMode) {
                rowCount =
                  totalNumberOfRows > 0 && rowData?.length > 0
                    ? isLastPage
                      ? groupCount
                      : totalNumberOfRows
                    : -1;
              } else {
                rowCount = filteredGroupCount;
              }
              agGrid.api.applyServerSideRowData({
                successParams: {
                  rowData: [],
                  rowCount,
                },
              });
            } else {
              if (!isLastPage && totalNumberOfRows > 0) {
                agGrid.api.setRowCount(totalNumberOfRows, true);
              }
            }

            if (!isChildRowsRequest) {
              store.dispatch(setLoadingRowCount(false));
            }
          };

          const isDoingGrouping = groupsPayload.rowGroupCols.length > 0;

          const extractCount = (r, failValue) => {
            try {
              return r.count;
            } catch (e) {
              console.error(e);
              return failValue;
            }
          };

          const requests = [
            post(`dataset/${fileId}/count/rows`, payload).then((r) =>
              extractCount(r, ROW_COUNT_FAILED)
            ),
          ];

          if (isDoingGrouping) {
            requests.push(
              post(`dataset/${fileId}/count/groups`, groupsPayload).then((r) =>
                extractCount(r, null)
              )
            );
          }

          Promise.all(requests).then(([filteredRowCount, filteredGroupCount]) => {
            updateCounts(filteredRowCount, filteredGroupCount);
          });
        } catch (error) {
          ShowToast({
            type: TOAST_TYPE_ERROR,
            text: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
            errorContext: error,
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
          });
        }
      };

      const { filterModel, sortModel, columnHighlights, columnState } =
        state.spreadsheet.clientState;
      const { isRanByCypress } = state.shared;
      const { user } = state.userData;

      params.request.filterModel = filterModel;
      params.request.sortModel = sortModel;
      params.request.endRow = params.request.startRow + paginationPageSize;

      if (!params.request.pivotMode) {
        params.request.pivotCols = [];
      }
      params.request.groupKeys = params.request.groupKeys?.map((row) => {
        return (row === 'true' || row === 'false') &&
          params.parentNode?.rowGroupColumn?.colDef?.fieldType === 'Boolean'
          ? JSON.parse(row.toLowerCase())
          : row;
      });
      this.state = params.request;

      const currentAppliedFilters = getCurrentAppliedFilters(filterModel?._cnf_) || [];
      const appliedFiltersMap = {};
      currentAppliedFilters.forEach((filter) => (appliedFiltersMap[filter.colId] = true));
      store.dispatch(setAppliedFiltersMap(appliedFiltersMap));

      const fileId = getFileUuidFromPath();

      let fileRowsResponse = null;

      async function enhancedFilterPost(route, data) {
        let longerThanUsualTimeoutId;
        let gatewayTimeoutId;
        const abortController = new AbortController();
        const refreshPage = () => window.location.reload();
        const getContactSupport = () => window?.fcWidget.open();

        const showLongerThanUsualMessage = (endpoint, payload) => {
          const customBody = () => (
            <div className='pl-2'>
              It’s taking longer than usual to load your results. You can wait it out, try{' '}
              <span className='text-ocean-blue-800 ' onClick={refreshPage}>
                refreshing the page
              </span>
              , or{' '}
              <span className='text-ocean-blue-800 ' onClick={getContactSupport}>
                contact support.
              </span>
            </div>
          );
          ShowToast({
            type: 'info',
            customBody,
            endpoint,
            payload,
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
            autoClose: false,
            closeButton: true,
            closeOnClick: true,
          });

          store.dispatch(
            addMixpanelEvent({
              eventName: 'Longer than usual results message shown',
              eventProps: {},
            })
          );
        };

        const showGatewayTimeoutMessage = (endpoint, payload) => {
          const customBody = () => (
            <div>
              Failed to load results. You can try{' '}
              <span className='text-ocean-blue-800 ' onClick={refreshPage}>
                refreshing the page
              </span>
              , or you can{' '}
              <span className='text-ocean-blue-800 ' onClick={getContactSupport}>
                contact support
              </span>{' '}
              for assistance.
            </div>
          );
          ShowToast({
            type: TOAST_TYPE_ERROR,
            customBody,
            endpoint,
            payload,
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
          });
          store.dispatch(
            addMixpanelEvent({
              eventName: 'Failed to load results message shown',
              eventProps: {},
            })
          );
        };

        try {
          longerThanUsualTimeoutId = setTimeout(
            () => showLongerThanUsualMessage(route, data),
            50000
          );
          gatewayTimeoutId = setTimeout(() => showGatewayTimeoutMessage(route, data), 90000);

          let response = null;
          if (isPivotMode || isLiveQuery) {
            response = await post(route, data, { signal: abortController.signal });
          } else {
            const { finalResponse } = await streamPost(
              route,
              data,
              {
                onProgress: (payload) => {
                  window.dispatchEvent(
                    new CustomEvent(EVENT_NAME_GIGASHEET_FILTER_PROGRESS, { detail: payload })
                  );
                },
              },
              {
                signal: abortController.signal,
              }
            );
            store.dispatch(setFilterProgressIsRunning(true));
            response = await finalResponse;
            store.dispatch(setFilterProgressIsRunning(false));
          }

          clearTimeout(longerThanUsualTimeoutId);
          clearTimeout(gatewayTimeoutId);

          return response;
        } catch (err) {
          clearTimeout(longerThanUsualTimeoutId);
          clearTimeout(gatewayTimeoutId);
          if (err.name === 'AbortError') {
            showGatewayTimeoutMessage(route, data);
            store.dispatch(
              addMixpanelEvent({
                eventName: 'Failed to load results message shown Abort controller',
                eventProps: {},
              })
            );
          }

          console.error(`Enhanced POST Filter error: ${err.message}`);
        }
      }

      const fileAndFunctionName = '__FILE_AND_FUNCTION_NAME__';
      let tokenExpirationDetails = {};
      if (!isRanByCypress && !user?.is_anonymous) {
        try {
          let token = store.getState()[authenticationSliceName].auth0IdToken;
          if (!token || typeof token !== 'string') {
            try {
              await silentRefresh(true, fileAndFunctionName);
            } catch (e) {
              return doFail();
            }
            token = store.getState()[authenticationSliceName].auth0IdToken;
            if (!token || typeof token !== 'string') {
              throw new Error('Token is not available or not a string');
            }
          }
          let tokenDetails = ExtractTokenDetails(token);
          if (tokenDetails.willExpireSoon) {
            try {
              await silentRefresh(true, fileAndFunctionName);
            } catch (e) {
              return doFail();
            }
            token = store.getState()[authenticationSliceName].auth0IdToken;
            tokenDetails = ExtractTokenDetails(token);
          }
          tokenExpirationDetails = {
            tokenExpireTime: tokenDetails.tokenExpireTime,
            tokenTimeRemainingUntilExpiration: tokenDetails.tokenTimeRemainingUntilExpiration,
            willExpireSoon: tokenDetails.willExpireSoon,
          };

          if (tokenDetails.willExpireSoon) {
            throw new Error('Token is still expiring soon even after silent refresh.');
          }
        } catch (error) {
          ShowToast({
            type: TOAST_TYPE_ERROR,
            text: 'Authentication error. Redirecting to login page in 5 seconds...',
            errorContext: error,
            fileAndFunction: fileAndFunctionName,
            other: tokenExpirationDetails,
          });
          setTimeout(async () => {
            await logout({ client_id: AUTH0_CLIENT_ID });
          }, 5000);
          return doFail();
        }
      }

      const requestCopy = { ...params.request };
      requestCopy.rowGroupCols = requestCopy.rowGroupCols.map((col) => {
        const column = columnState.find((e) => e.colId === col.id);
        return {
          ...col,
          groupFunc: column?.groupFunc ?? '',
        };
      });

      requestCopy.pivotCols = requestCopy.pivotCols.map((col) => {
        const column = columnState.find((e) => e.colId === col.id);
        return {
          ...col,
          groupFunc: column?.groupFunc ?? '',
        };
      });

      const payload = {
        ...requestCopy,
        columnHighlights: columnHighlights || {},
      };
      if (isPivotMode) {
        const endpoint = `${FILE}/${fileId}/${PIVOT}`;
        fileRowsResponse = await enhancedFilterPost(endpoint, payload);

        if (fileRowsResponse.Success === false) {
          store.dispatch(
            addToast({
              id: Date.now(),
              message: fileRowsResponse.Message,
              type: TOAST_TYPE_ERROR,
              wasShown: false,
              fileAndFunction: fileAndFunctionName,
              endpoint: endpoint,
              payload: payload,
              responseStatus: fileRowsResponse?.status,
            })
          );
        }
        this.setSecondaryColsIntoGrid(fileRowsResponse.pivotFields || []);
      } else {
        let endpoint = `${FILE}/${fileId}/${FILTER}/stream-progress`;
        if (isLiveQuery) {
          endpoint = `${FILE}/${fileId}/${FILTER}`;
        }
        fileRowsResponse = await enhancedFilterPost(endpoint, payload);
        if (fileRowsResponse?.status === StatusCodes.FORBIDDEN) {
          // The 403 (FORBIDDEN) status indicates that the Auth0 user session might have expired.
          // Auth0 sessions can expire independently of token expiration, often after 3 or 7 days depending on configuration.
          // When this happens, a login is required even if the token is refreshed.
          // We attempt a silent token refresh using `silentRefresh(true)` to confirm whether the session is still valid.
          // If silent refresh fails (throws an error), it confirms that the session has expired, and the user must log in again.
          try {
            await silentRefresh(true, fileAndFunctionName);
          } catch (e) {
            return doFail();
          }
          // try again if silentRefresh did not throw
          fileRowsResponse = await enhancedFilterPost(endpoint, payload);
        }

        store.dispatch(setColumnHighlights(fileRowsResponse?.cellHighlights || {}));

        if (!fileRowsResponse.rows && fileRowsResponse?.status !== StatusCodes.NOT_FOUND) {
          store.dispatch(
            addToast({
              id: Date.now(),
              message: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
              type: TOAST_TYPE_ERROR,
              wasShown: false,
              fileAndFunction: fileAndFunctionName,
              endpoint: endpoint,
              payload: payload,
              responseStatus: fileRowsResponse?.status,
              other: tokenExpirationDetails,
            })
          );
          return doFail();
        }
        this.colDefHash = null;
        if (agGrid.api.getPivotColumns().length === 0) {
          this.setSecondaryColsIntoGrid(null);
        }

        if (
          fileRowsResponse.message === 'Bad Request' &&
          fileRowsResponse?.status !== StatusCodes.NOT_FOUND
        ) {
          store.dispatch(
            addToast({
              id: Date.now(),
              message: 'Unable to apply filter',
              type: TOAST_TYPE_ERROR,
              wasShown: false,
              fileAndFunction: fileAndFunctionName,
              endpoint: endpoint,
              payload: payload,
              responseStatus: fileRowsResponse?.status,
              other: tokenExpirationDetails,
            })
          );

          fileRowsResponse = {
            lastRow: 0,
            rows: [],
          };
        }
      }

      const rowData =
        fileRowsResponse.rows?.map((row) =>
          Object.keys(row).reduce((acc, key) => {
            if (row[key] === true) {
              acc[key] = 'true';
            } else if (row[key] === false) {
              acc[key] = 'false';
            } else acc[key] = row[key];
            return acc;
          }, {})
        ) || [];

      const isLastPage = rowData.length < paginationPageSize;
      getPaginationData(payload, isLastPage, fileRowsResponse.lastRow, rowData);

      params.success({
        rowData,
      });
    },
    setSecondaryColsIntoGrid: function (secondaryColDefs) {
      const colDefHash = createColsHash(secondaryColDefs);
      if (this.colDefHash !== colDefHash) {
        agGrid.api.setPivotResultColumns(secondaryColDefs);
        this.colDefHash = colDefHash;
      }
    },
  };
};

const createColsHash = (colDefs) => {
  if (!colDefs) {
    return null;
  }
  const parts = [];
  colDefs.forEach((colDef) => {
    if (colDef.children) {
      parts.push(colDef.groupId);
      parts.push('[' + createColsHash(colDef.children) + ']');
    } else {
      parts.push(colDef.colId);
      // headerName can change if the aggFunc was changed in a value col. if we didn't
      // do this, then the grid would not pick up on new header names as we move from
      // eg min to max.
      if (colDef.headerName) {
        parts.push(colDef.headerName);
      }
    }
  });
  return parts.join(',');
};

export const getMainMenuItems = () => {};

export const frameworkComponents = {
  agColumnHeader: CustomColumnHeader,
  customNoRowsOverlay: CustomNoRowsComponent,
  customPinnedRowRenderer: (props) => <AggregationFunctions agGrid={props} column={props.column} />,
  aggFunctionSelector: (props) => (
    <FunctionSelect
      agGrid={props}
      column={props.column}
      value={props.value}
      isCellAggSelector={true}
      valueFormatted={props.valueFormatted}
    />
  ),
};

export const modifyRowDataAppearance = (rowData) => {
  const modifiedRowData = [];
  if (rowData) {
    Object.keys(rowData).forEach((key) => {
      modifiedRowData[key] = (rowData[key] + '').replaceAll('\\r\\n', '\n').replaceAll('\\t', '\t');
    });
  }
  return modifiedRowData;
};

export const filterContextMenuAction = (params, initialType) => {
  let type = initialType;
  const fieldType =
    params.column.colId === AG_GRID_AUTO_COLUMN
      ? params.node.rowGroupColumn.colDef.fieldType
      : params.column.colDef.fieldType;
  const filterType = getFilterType(fieldType);
  let filterValue = params.value;
  const columnId =
    params.column.colId === AG_GRID_AUTO_COLUMN ? params.node.field : params.column.colId;

  // have to check because ag-grid returns it as boolean type(ag-grid bug)
  if (params.value === true) {
    filterValue = 'true';
  }
  if (filterType === 'number') {
    filterValue = String(params.value);
  }
  if (fieldType === 'DateTime' || fieldType === 'DateTime64') {
    filterValue = Math.floor(new Date(params.value + 'Z').getTime() / 1000).toString();
  }

  if (fieldType === 'IPv4') {
    type = initialType === 'equalsAny' ? 'IPv4-Equals-Any' : 'IPv4-Not-Equals-Any';
  }

  if (fieldType === 'DateTime' || fieldType === 'DateTime64' || fieldType === 'Boolean') {
    type = initialType === 'equalsAny' ? 'equals' : 'notEqual';
  }

  if (!params.value) {
    type = initialType === 'equalsAny' ? 'isBlank' : 'isNotBlank';
  }

  return [
    {
      colId: columnId,
      type,
      filter: type.includes('Any') ? [filterValue] : filterValue,
      filterType,
    },
  ];
};

export const autoGroupColumnDef = {
  minWidth: 246,
  pinned: 'left',
  lockPosition: true,
  filter: true,
  cellRendererParams: {
    filter: true,
    suppressCount: true,
    innerRenderer: CustomAutoGroupValue,
  },
  cellRendererSelector: (params) => {
    if (params.api.getColumnGroupState().length === 1) {
      return { component: CustomAutoGroupValue };
    }
    return { component: 'agGroupCellRenderer' };
  },
};

export const trackMixpanelColumnVisibility = () => {
  const { dispatch } = store;
  dispatch(
    addMixpanelEvent({
      eventName: 'Hide/Show Column',
      eventProps: {},
      userIncrementName: '# of Hide/Show Columns',
    })
  );
};

export const getGridRequestData = (agGrid) => {
  if (!agGrid) return;
  if (
    agGrid &&
    agGrid.api.getGridOption('serverSideDatasource')?.state &&
    Object.keys(agGrid.api.getGridOption('serverSideDatasource')?.state).length > 0
  ) {
    return agGrid.api.getGridOption('serverSideDatasource')?.state;
  }
  return {};
};
export const flagRows = async (params, flag, agGrid) => {
  const uuid = getFileUuidFromPath();

  if (params?.node?.group) {
    const agGridRequest = agGrid.api.getGridOption('serverSideDatasource')?.state;
    agGridRequest.groupKeys = [params.node.key];
    agGridRequest.rowGroupCols = agGridRequest.rowGroupCols.filter(
      (e) => e.id === params.node.field
    );
    const payload = {
      ag_grid_request: agGridRequest,
      flag: flag,
    };
    await put(`${URL_DATASET}/${uuid}/flag/query`, payload);
  } else {
    const payload = { flags: {} };
    payload.flags[params.node.data.A] = flag;
    await put(`${URL_DATASET}/${uuid}/flag`, payload);
  }

  agGrid.api.closeToolPanel();
  agGrid.api.deselectAll();
  agGrid.api.refreshServerSide();
};

const changeHeadersAndFirstRow = async (
  callback,
  mixpanelMessage,
  dispatch,
  agGrid,
  currentFile,
  setIsInsertingNewColumn
) => {
  const { isFileShared } = currentFile;
  const uuid = getFileUuidFromPath();

  if (!agGrid) return;
  if (isFileShared) {
    dispatch(showModal({ name: MODAL_MAKE_COPY }));
  }
  try {
    dispatch(setIsInsertingNewColumn(true));
    const result = await callback(uuid);

    if (!result.Success) {
      return store.dispatch(
        addToast({
          id: Date.now(),
          message: `${result.Message}`,
          type: 'error',
          wasShown: false,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        })
      );
    }

    await dispatch(fetchOpenedFile(uuid));
    dispatch(addMixpanelEvent({ eventName: `Set ${mixpanelMessage}` }));
    agGrid.api.refreshServerSide();
  } catch (error) {
    throw new Error(error);
  } finally {
    dispatch(setIsInsertingNewColumn(false));
  }
};

export const headerAsFirstRow = (dispatch, agGrid, currentFile) => {
  changeHeadersAndFirstRow(
    (uuid) => post(`${URL_DATASET}/${uuid}/headersasfirstrow`, {}, true),
    'header as first row',
    dispatch,
    agGrid,
    currentFile,
    setIsInsertingNewColumn
  );
};

export const firstRowAsHeader = (dispatch, agGrid, currentFile) => {
  changeHeadersAndFirstRow(
    (uuid) => put(`${URL_DATASET}/${uuid}/firstrowisheader`, {}, true),
    'first row as header',
    dispatch,
    agGrid,
    currentFile,
    setIsInsertingNewColumn
  );
};

export const handleDeleteDuplicates = async (columns) => {
  const uuid = getFileUuidFromPath();
  const { dispatch } = store;
  const state = store.getState();
  const {
    clientState: { sortModel },
  } = state.spreadsheet;

  try {
    dispatch(setIsInsertingNewColumn(true));

    const endpoint = `${URL_DATASET}/${uuid}`;
    const payload = {
      sortModel,
      columns,
    };
    const deleteDuplicates = await httpDeleteWithBody(endpoint, 'deduplicate-rows', payload);

    deleteDuplicates.Success
      ? dispatch(setIsDeletingRows(true))
      : store.dispatch(
          addToast({
            id: Date.now(),
            message: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
            type: 'error',
            wasShown: false,
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
            endpoint: endpoint,
            payload: payload,
          })
        );
  } catch (e) {
    throw new Error(e);
  } finally {
    dispatch(setIsInsertingNewColumn(false));
    dispatch(setIsDeletingRows(false));
  }
};

export const chartData = (agGrid) => {
  try {
    const cells = agGrid.api?.getCellRanges();

    const { endRow, startRow, columns } = cells?.[0] ?? {};
    const isAllRowsSelected = startRow?.rowIndex === 0 && endRow?.rowIndex === 0;
    if (isAllRowsSelected) {
      endRow.rowIndex = agGrid.api?.paginationGetPageSize() - 1;
    }

    agGrid.api.createRangeChart({
      chartType: 'stackedColumn',
      cellRange: {
        rowStartIndex: startRow?.rowIndex,
        rowEndIndex: endRow?.rowIndex,
        columns: columns?.map((i) => i.colId),
      },
      chartThemeOverrides: {
        common: {
          title: {
            enabled: true,
            text: 'Chart Editor',
          },
        },
      },
    });
  } catch (error) {
    ShowToast({
      type: TOAST_TYPE_ERROR,
      text: 'Failed to display chart',
      errorContext: error,
      fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
    });
  }
};

export const addRow = async (
  rowCorrectingIndex = 0,
  agGrid,
  isPreviewMode,
  showPreviewModeUpsellModal
) => {
  if (isPreviewMode) {
    return showPreviewModeUpsellModal();
  }
  const cellRanges = agGrid?.api?.getCellRanges();
  const { topRowIndex, rowCount } = getTopRangeRowsIndexAndRowCount(cellRanges);

  const { dispatch } = store;
  const uuid = getFileUuidFromPath();

  dispatch(setIsInsertingNewColumn(true));

  const payload = {
    index: topRowIndex + 1 + rowCorrectingIndex,
    num_rows: rowCount,
  };

  try {
    const endpoint = `datasets/${uuid}/insert-blank-row`;
    const res = await post(`datasets/${uuid}/insert-blank-row`, payload);

    if (!res.Success) {
      return dispatch(
        addToast({
          id: Date.now(),
          message: `${res.Message}`,
          type: 'error',
          wasShown: false,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
          endpoint: endpoint,
          payload: payload,
        })
      );
    }

    dispatch(
      addMixpanelEvent({
        eventName: 'Insert Blank Row',
        eventProps: { Location: 'Right click on row' },
        userIncrementName: '# of group by use',
      })
    );

    await dispatch(fetchOpenedFile(uuid));
  } catch (error) {
    throw new Error(error);
  } finally {
    agGrid.api.refreshServerSide();
    dispatch(setIsInsertingNewColumn(false));
  }
};

export const handleDeleteRow = async (rowsToDelete = '', requestBody = {}) => {
  const { dispatch } = store;
  const state = store.getState();
  const uuid = getFileUuidFromPath();
  const { selectedRow } = state.spreadsheet;
  const { isPreviewMode } = state.dataset;
  if (isPreviewMode) {
    return showPreviewModeUpsellModal();
  }
  dispatch(setIsDeletingRows(true));

  const endpoint = `${URL_DATASET}/${uuid}`;
  const payload = rowsToDelete ? requestBody : { rows: [Number(selectedRow?.rowData?.A)] };
  const response = await httpDeleteWithBody(endpoint, `delete-rows${rowsToDelete}`, payload);

  if (response.Success) {
    // Remove filter after rows filtered/unfiltered rows are deleted
    if (rowsToDelete) {
      dispatch(setReduxFilterModelCnf([]));
    }
  } else {
    store.dispatch(
      addToast({
        id: Date.now(),
        message: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
        type: 'error',
        wasShown: false,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
        endpoint: endpoint,
        payload: payload,
      })
    );
  }
  dispatch(setIsDeletingRows(false));
};

export const handleDeleteColumns = async (columnsToDelete = '', columnsToDeleteList = []) => {
  const { dispatch } = store;
  const {
    columnDefs,
    clientState: { columnState, sidePanel },
  } = store.getState().spreadsheet;
  const uuid = getFileUuidFromPath();

  if (
    columnsToDelete?.length > 0 &&
    [DELETE_VISIBLE_COLUMNS, DELETE_HIDDEN_COLUMNS, DELETE_SELECTED_COLUMNS].includes(
      columnsToDelete
    )
  ) {
    dispatch(setIsInsertingNewColumn(true));
    const hiddenColIds = columnState.filter((e) => e.hide).map((e) => e.colId);
    let visibleColIds = columnState.filter((e) => !e.hide).map((e) => e.colId);
    const selectedColId = columnState
      .filter((e) => columnsToDeleteList.some((el) => e.colId === el.colId))
      .map((e) => e.colId);

    // new or unmodified files have an empty column state, so we need to instead use the columnDefs
    if (!(columnState?.length > 0)) {
      visibleColIds = columnDefs.map((e) => e.colId);
    }

    const colIdsToDelete =
      columnsToDelete === DELETE_HIDDEN_COLUMNS
        ? hiddenColIds
        : columnsToDelete === DELETE_SELECTED_COLUMNS
        ? selectedColId
        : visibleColIds;

    // if exists, remove column A from colIdsToDelete
    const index = colIdsToDelete.indexOf(ROW_NUMBER_COLUMN_ID);
    if (index > -1) {
      colIdsToDelete.splice(index, 1);
    }

    if (colIdsToDelete.length > 0) {
      let endpoint = '';
      let payload = {};
      try {
        endpoint = `${FILES}/${uuid}/${DELETE_MULTIPLE_COLUMNS}`;
        payload = {
          columnsToDelete: colIdsToDelete,
        };
        const response = await postRaw(endpoint, payload);
        if (response.status === 200) {
          await dispatch(fetchOpenedFile(uuid));
          dispatch(deleteColumns(colIdsToDelete));
          dispatch(setReduxFilterModelCnf([]));
          dispatch(restoreGridState({ sidePanel }));
        } else {
          store.dispatch(
            addToast({
              id: Date.now(),
              message: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
              type: 'error',
              wasShown: false,
              fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
              endpoint: endpoint,
              payload: payload,
            })
          );
        }
      } catch (e) {
        store.dispatch(
          addToast({
            id: Date.now(),
            message: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
            type: 'error',
            wasShown: false,
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
            endpoint: endpoint,
            payload: payload,
          })
        );
      }
    }
    dispatch(setIsInsertingNewColumn(false));
  }
};

export const deleteRow = (rowsToDelete, requestBody) => {
  const state = store.getState();
  const { dispatch } = store;
  const {
    clientState: { alwaysConfirmDeleteRow },
  } = state.spreadsheet;

  alwaysConfirmDeleteRow
    ? dispatch(showModal({ name: MODAL_DELETE_ROW, props: { rowsToDelete, requestBody } }))
    : handleDeleteRow(rowsToDelete, requestBody);
};

export const openFormulaPopover = ({ resetForm, mode, columnId, formulaToEdit }) => {
  const buttonElement = document.querySelector(QUERY_SELECTOR_FORMULA_PANEL_BTN);

  if (buttonElement) {
    store.dispatch(setNewColumnMode(mode || 'blank'));
    store.dispatch(setInsertNewColumnNextTo(columnId || null));
    store.dispatch(setResetFormulaForm(!!resetForm));

    if (formulaToEdit) {
      formulaToEdit['columnId'] = columnId;
      store.dispatch(setFormulaToEdit(formulaToEdit));
    }

    buttonElement.click();
  }
};

export const openHideColumnsPopover = () => {
  const buttonElement = document.querySelector('[data-cy="toolpanel-manage-columns"]');
  if (buttonElement) {
    buttonElement.click();
  }
};

export const generateDeletingRowsArray = async (params) => {
  const cellRanges = params.api.getCellRanges();
  const cellRange = {
    columns: [ROW_NUMBER_COLUMN_ID],
  };
  let rowData = [];
  params.api.forEachNode((node) => {
    if (node.group && node.expanded) {
      const groupNode = params.api.getRowNode(node.id);
      if (groupNode.childrenAfterFilter && Array.isArray(groupNode.childrenAfterFilter)) {
        groupNode.childrenAfterFilter.forEach((childNode) => {
          const rowDataItem = {};
          cellRange.columns.forEach((column) => {
            rowDataItem[column] = childNode.data[column];
            rowDataItem['rowIndex'] = childNode.rowIndex;
          });
          rowData.push(rowDataItem);
        });
      }
    } else if (!node.group && params.api.getRowGroupColumns().length) {
      const rowDataItem = {};
      cellRange.columns.forEach((column) => {
        rowDataItem[column] = node.data[column];
        rowDataItem['rowIndex'] = node.rowIndex;
      });
      rowData.push(rowDataItem);
    }
  });

  if (!params.api.getRowGroupColumns().length) {
    const selectedRanges = params.api.getCellRanges();
    const dataToDelete = [];
    selectedRanges.forEach((cellRange) => {
      const { startRow, endRow } = cellRange;
      const fromRowIndex = Math.min(startRow.rowIndex, endRow.rowIndex);
      const toRowIndex = Math.max(startRow.rowIndex, endRow.rowIndex);

      for (let rowIndex = fromRowIndex; rowIndex <= toRowIndex; rowIndex++) {
        const rowNode = params.api.getDisplayedRowAtIndex(rowIndex);
        if (rowNode) {
          dataToDelete.push(Number(rowNode.data.A));
        }
      }
    });
    rowData = dataToDelete;
  }
  if (Array.isArray(rowData) && rowData.length > 0 && typeof rowData[0] === 'object') {
    return rowData
      .filter((item) => generateArrayFromRowsIndex(cellRanges).includes(item.rowIndex))
      .map((el) => Number(el.A));
  } else {
    return rowData;
  }
};

export const handleDeleteRows = async (rows) => {
  const { dispatch } = store;
  dispatch(setIsInsertingNewColumn(true));
  const uuid = getFileUuidFromPath();

  dispatch(setIsDeletingRows(true));

  try {
    const endpoint = `${URL_DATASET}/${uuid}`;
    const payload = {
      rows,
    };
    const response = await httpDeleteWithBody(endpoint, 'delete-rows', payload);
    if (response.Success) {
      dispatch(setIsInsertingNewColumn(false));
    } else {
      store.dispatch(
        addToast({
          id: Date.now(),
          message: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
          type: 'error',
          wasShown: false,
          fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
          endpoint: endpoint,
          payload: payload,
        })
      );
      dispatch(setIsInsertingNewColumn(false));
    }
  } catch (e) {
    dispatch(setIsInsertingNewColumn(false));
  } finally {
    dispatch(setGenerateDeletingRows(false));

    dispatch(setIsDeletingRows(false));
  }
};

export const hasNoWritePermission = (currentFile) => {
  return !currentFile?.availablePermissions?.includes(PERMISSION_WRITE);
};

export const isBlankOptionVisible = (type, operator) => {
  return !(
    (type === 'Int64' ||
      type === 'UInt64' ||
      type === 'Float64' ||
      type === 'String' ||
      type === 'ZipCode' ||
      type === 'EmailAddress') &&
    (operator === 'equalsAny' || operator === 'notEqualsAny')
  );
};

export const generatePathForNode = (node) => {
  const path = [];
  while (node && node.key !== null) {
    path.unshift(node.key);
    node = node.parent;
  }
  return path;
};

export const openSheetViewPopover = () => {
  setTimeout(() => {
    const element = document.getElementById('view-button');
    const button = element.querySelector('button');
    button.click();
  }, 500);
};
